'use client';

import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';

import { cn } from '@/hen-ui/utils';
import { usePortalContainer } from '@/hen-ui/providers/PortalContainerContext';
import {
  TINTED_BACKDROP_CLASS,
  DEFAULT_BACKDROP_CLASS,
  type DialogBackdrop,
} from './dialog';

function Drawer({
  disablePreventScroll = false,
  modal = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) {
  return (
    <DrawerPrimitive.Root
      data-slot='drawer'
      modal={modal}
      disablePreventScroll={disablePreventScroll}
      {...props}
    />
  );
}

function DrawerTrigger({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
  return <DrawerPrimitive.Trigger data-slot='drawer-trigger' {...props} />;
}

function DrawerPortal({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
  const portalContainer = usePortalContainer();
  return (
    <DrawerPrimitive.Portal
      data-slot='drawer-portal'
      container={portalContainer}
      {...props}
    />
  );
}

function DrawerClose({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Close>) {
  return <DrawerPrimitive.Close data-slot='drawer-close' {...props} />;
}

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn(
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-modal bg-black/50',
      className,
    )}
    {...props}
  />
));

DrawerOverlay.displayName = 'DrawerOverlay';

type DrawerContentProps = React.ComponentProps<
  typeof DrawerPrimitive.Content
> & {
  /**
   * Optional actions aligned to the end of the bottom-sheet pull bar (mobile).
   * With `headerTitle`, they sit at the end of the title row instead so they
   * line up with the title.
   */
  bottomBarEnd?: React.ReactNode;
  /** Paints the whole drawer. `dark` re-points text roles to on-dark colors. */
  surface?: 'default' | 'dark' | null;
  /** Renders a title row that stays pinned while the body scrolls and names the drawer. */
  headerTitle?: React.ReactNode;
  /** Padding / spacing classes for the sticky title row. Unset keeps `px-md pb-md`. */
  headerClassName?: string;
  /** When set, wraps `children` in a body element carrying these classes. */
  bodyClassName?: string;
  /** Classes merged onto the backdrop behind the drawer. */
  overlayClassName?: string;
  /** Bottom-sheet drag handle. Unset keeps it; set false to hide. */
  showHandle?: boolean;
  /** `tinted` dims with the theme's dark neutral instead of black. */
  backdrop?: DialogBackdrop | null;
};

function hasTitle(title: React.ReactNode): boolean {
  return title != null && title !== '';
}

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  DrawerContentProps
>(
  (
    {
      className,
      children,
      bottomBarEnd,
      surface,
      headerTitle,
      headerClassName,
      bodyClassName,
      overlayClassName,
      showHandle = true,
      backdrop,
      ...props
    },
    ref,
  ) => (
    <DrawerPortal data-slot='drawer-portal'>
      <DrawerOverlay
        className={cn(
          backdrop === 'tinted'
            ? TINTED_BACKDROP_CLASS
            : DEFAULT_BACKDROP_CLASS,
          overlayClassName,
        )}
      />
      <DrawerPrimitive.Content
        ref={ref}
        data-vaul-custom-container='true'
        className={cn(
          'group/drawer-content fixed z-modal flex h-auto flex-col overflow-hidden bg-white',
          surface === 'dark' && 'surface-dark bg-surface-chrome',
          'data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[80vh]',
          'data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[80vh] data-[vaul-drawer-direction=bottom]:rounded-t-lg',
          'data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4',
          'data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4',
          className,
        )}
        {...props}
      >
        <div
          data-slot='drawer-top-row'
          className={cn(
            'relative hidden w-full shrink-0 items-center justify-end px-md pb-min pt-md',
            'group-data-[vaul-drawer-direction=bottom]/drawer-content:flex',
            !showHandle &&
              (!bottomBarEnd || hasTitle(headerTitle)) &&
              'group-data-[vaul-drawer-direction=bottom]/drawer-content:hidden',
          )}
        >
          {showHandle ? (
            <div
              data-slot='drawer-handle'
              className='pointer-events-none absolute left-1/2 top-1/2 h-xxs w-24 -translate-x-1/2 -translate-y-1/2 rounded-max bg-neutral-200'
              aria-hidden
            />
          ) : null}
          {hasTitle(headerTitle) ? null : bottomBarEnd}
        </div>
        <div className='flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
          {hasTitle(headerTitle) ? (
            <div
              data-slot='drawer-title-row'
              className={cn(
                'sticky top-0 z-10 flex shrink-0 items-center justify-between gap-xs',
                headerClassName ?? 'px-md pb-md',
                surface === 'dark' ? 'bg-surface-chrome' : 'bg-white',
              )}
            >
              <DrawerPrimitive.Title
                data-slot='dialog-title'
                className='min-w-0 flex-1 text-h4 font-bold font-heading text-heading'
              >
                {headerTitle}
              </DrawerPrimitive.Title>
              {bottomBarEnd}
            </div>
          ) : null}
          {bodyClassName ? (
            <div data-slot='drawer-body' className={bodyClassName}>
              {children}
            </div>
          ) : (
            children
          )}
        </div>
      </DrawerPrimitive.Content>
    </DrawerPortal>
  ),
);

DrawerContent.displayName = 'DrawerContent';

function DrawerHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex flex-col gap-0.5 p-4 group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center group-data-[vaul-drawer-direction=top]/drawer-content:text-center md:gap-1.5 md:text-left',
        className,
      )}
      {...props}
    />
  );
}

function DrawerFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('mt-auto flex flex-col gap-2 p-4', className)}
      {...props}
    />
  );
}

function DrawerTitle({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Title>) {
  return (
    <DrawerPrimitive.Title
      className={cn('text-foreground font-semibold', className)}
      {...props}
    />
  );
}

function DrawerDescription({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) {
  return (
    <DrawerPrimitive.Description
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
